import { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  useMutation,
  useMutationState,
  useQueryClient,
} from "@tanstack/react-query";

import type { AnswerQuestionDto } from "~/data/user-test/answer-question.dto";
import type {
  UserTestAnswerDto,
  UserTestQuestionDto,
} from "~/data/user-test/user-test.dto";

import { answerChecklistQuestion } from "~/data/user-test/user-test.service";

import { Button } from "../shared/button";
import { HtmlContent } from "../shared/html-content";
import { EmojiSmileIcon } from "../shared/icons/emoji-smile";
import { EmojiFrownIcon } from "../shared/icons/emoji-frown";
import { EmojiExpressionlessIcon } from "../shared/icons/emoji-expressionless";

interface Props {
  questions: UserTestQuestionDto[];
  answers: UserTestAnswerDto[];
  onFinish: () => void;
  finishing: boolean;
}
export function ChecklistQuestions({
  questions,
  answers,
  onFinish,
  finishing,
}: Props) {
  const initialAnswersState = useMemo(
    () => new Map(answers.map((it) => [it.test_question_id, it])),
    [answers]
  );
  const sentAnswers = useMutationState({
    filters: { mutationKey: ["answer_checklist"] },
    select: (mutation) => mutation.state.variables as AnswerQuestionDto,
  });
  const uniqueSentAnswers = useMemo(
    () =>
      sentAnswers.reduce((map, it) => {
        map.set(it.test_question_id, it);
        return map;
      }, new Map<string, AnswerQuestionDto>()),
    [sentAnswers]
  );
  const answeredQuestionsIds = Array.from(
    new Set([...initialAnswersState.keys(), ...uniqueSentAnswers.keys()])
  );

  const totalScore = useMemo(
    () =>
      questions.reduce((total, question) => {
        const answerDto = uniqueSentAnswers.get(question.id);
        const answer = answerDto
          ? question.question_answers.find(
              (it) => it.id === answerDto.test_question_answer_id
            )
          : initialAnswersState.get(question.id);
        const score = answer?.score ?? 0;
        return total + score;
      }, 0),
    [initialAnswersState, questions, uniqueSentAnswers]
  );

  return (
    <>
      <div className="grid gap-5">
        {questions.map((item) => (
          <ChecklistQuestion
            key={item.id}
            question={item}
            answer={initialAnswersState.get(item.id)}
          />
        ))}
        <div className="bg-white shadow-card rounded-[10px] p-5 flex items-center justify-between">
          <h5 className="font-alternative font-semibold leading-5 uppercase ml-6">
            Total
          </h5>
          <div className="w-20 h-20 flex items-center justify-center rounded-[10px] bg-alternative-1 text-white font-alternative text-2xl font-semibold uppercase">
            {totalScore.toLocaleString("en-US", {
              style: "decimal",
              maximumFractionDigits: 2,
            })}
          </div>
        </div>
      </div>
      <div>
        <Button
          variant="primary-2"
          className="h-10 ml-auto w-60 mt-5"
          onClick={() => {
            if (questions.length > answeredQuestionsIds.length) {
              alert("Preencha todo o checklist para concluir!");
              return;
            }
            onFinish();
          }}
          loading={finishing}
        >
          Concluir
        </Button>
      </div>
    </>
  );
}

interface ChecklistQuestionProps {
  question: UserTestQuestionDto;
  answer?: UserTestAnswerDto;
}
function ChecklistQuestion({ question, answer }: ChecklistQuestionProps) {
  const params = useParams();
  const queryClient = useQueryClient();
  const answerMutation = useMutation({
    mutationKey: ["answer_checklist", question.id],
    mutationFn: (dto: AnswerQuestionDto) => answerChecklistQuestion(dto),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["user_test", params.id] });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["user_test", params.id],
      });
    },
  });
  const onAnswer = (answerId: string) =>
    answerMutation.mutate({
      test_id: params.id!,
      test_question_id: question.id,
      test_question_answer_id: answerId,
    });

  const answers = question.question_answers.slice(0, 3);
  const options = [
    {
      title: "Adequado",
      icon: <EmojiSmileIcon size={40} />,
      accent: "#07EC5B",
    },
    {
      title: "Parcial",
      icon: <EmojiExpressionlessIcon size={40} />,
      accent: "#00C2CB",
    },
    {
      title: "Inadequado",
      icon: <EmojiFrownIcon size={40} />,
      accent: "#FA4F3B",
    },
  ];

  if (answers.length === 2) {
    options.splice(1, 1); // Removes "Parcial" if there's only 2 answers
  }

  const selectedAnswerId =
    answerMutation.variables?.test_question_answer_id ??
    answer?.question_answer_id;

  return (
    <div className="border rounded-lg p-5 shadow-card flex flex-col">
      <div className="flex justify-between items-center">
        <div className="pl-5 font-alternative text-sm">
          <HtmlContent content={question.question} />
        </div>
        <div className="grid grid-flow-col gap-4 justify-end">
          {answers.map((it, i) => {
            const option = options[i];
            const selected = selectedAnswerId === it.id;
            return (
              <button
                key={it.id}
                disabled={selected}
                onClick={() => onAnswer(it.id)}
                className={`flex flex-col items-center justify-center gap-1 w-20 h-20 rounded-lg ${
                  selected ? "text-white" : "text-[#5B5B5B]"
                }`}
                style={{ background: selected ? option.accent : "white" }}
              >
                <span className="text-2xl">{option.icon}</span>
                <span className="text-[10px] uppercase font-bold font-alternative">
                  {selected ? it.score : option.title}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
